import { BlueprintFamily } from '../../../api/markets'

export type BlueprintEventTypeMismatch = {
  blueprintFamily: BlueprintFamily
  /** Types its active events use that it does not accept, counting the events still to review. */
  mismatchedTypeIds: { typeId: string; count: number }[]
  mismatchedEventCount: number
}

const hasAcceptedEventTypes = (blueprintFamily: BlueprintFamily) => (blueprintFamily.eventTypes?.length || 0) > 0

const blueprintsWithOddCases = (blueprintFamilies: BlueprintFamily[], accepted: boolean): BlueprintEventTypeMismatch[] =>
  blueprintFamilies
    .filter(hasAcceptedEventTypes)
    .map((blueprintFamily) => {
      const mismatched = (blueprintFamily.unacceptedEventTypes || [])
        .map((eventType) => ({ typeId: eventType.typeId, count: eventType.events.filter((event) => !!event.accepted === accepted).length }))
        .filter((eventType) => eventType.count > 0)
      return {
        blueprintFamily,
        mismatchedTypeIds: mismatched,
        mismatchedEventCount: mismatched.reduce((total, eventType) => total + eventType.count, 0),
      }
    })
    .filter((blueprint) => blueprint.mismatchedEventCount > 0)
    .sort(
      (a, b) =>
        b.mismatchedEventCount - a.mismatchedEventCount ||
        b.mismatchedTypeIds.length - a.mismatchedTypeIds.length ||
        (a.blueprintFamily.names.en || '').localeCompare(b.blueprintFamily.names.en || '')
    )

/**
 * Blueprints whose active events carry a type the blueprint does not accept, leaving out the events
 * an analyst accepted as exceptions.
 *
 * Only blueprints that have declared an accepted list are considered. Until an analyst declares one,
 * every type its events use reports as not accepted, which says nothing about the events being wrong.
 */
export const blueprintsWithEventTypeMismatches = (blueprintFamilies: BlueprintFamily[]): BlueprintEventTypeMismatch[] =>
  blueprintsWithOddCases(blueprintFamilies, false)

/**
 * Blueprints with odd cases an analyst accepted as exceptions, counting only those events. Most
 * first, like the mismatches.
 */
export const blueprintsWithAcceptedOddCases = (blueprintFamilies: BlueprintFamily[]): BlueprintEventTypeMismatch[] =>
  blueprintsWithOddCases(blueprintFamilies, true)

/**
 * Blueprints that have no accepted event types yet, which is where the curation still has to happen.
 */
export const blueprintsWithoutAcceptedEventTypes = (blueprintFamilies: BlueprintFamily[]): BlueprintFamily[] =>
  blueprintFamilies.filter((blueprintFamily) => !hasAcceptedEventTypes(blueprintFamily))
